import { Component, OnDestroy, OnInit, ViewChild, Inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { Subject } from 'rxjs';
import { Environment } from 'src/app/models/environment';
import { Team } from 'src/app/oncall/models/team';
import { EnvironmentService } from 'src/app/services/environment.service';
import { OnCallService } from 'src/app/oncall/services/oncall.service';
import { ToastService } from 'src/app/services/toast.service';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorDialogComponent } from 'src/app/dialog/error-dialog/error-dialog.component';
import { TeamMember } from 'src/app/oncall/models/team-member';
import { SelectionModel } from '@angular/cdk/collections';
import { TeamMemberDetails } from 'src/app/oncall/models/team-member-details';
import { AddTeamMemberDialogComponent } from '../dialog/add-team-member-dialog/add-team-member-dialog.component';
import { EscalationPolicy } from 'src/app/oncall/models/escalation-policy';
import { ProcessingWorkflow } from 'src/app/oncall/models/processing-workflow';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { SupportHours } from '../models/support-hours';
import { UserProfileService } from 'src/app/services/user-profile.service';
import { LoadingService } from 'src/app/services/loading.service';

@Component({
    selector: 'app-oncall-edit-team',
    templateUrl: './edit-team.component.html',
    styleUrls: ['./edit-team.component.css', '../../home/home.component.css'],
    standalone: false
})
export class EditTeamComponent implements OnInit, OnDestroy {
  environment: Environment;
  pageEnabled: boolean;
  teamName!: string;
  team!: Team;
  allTeams: Team[] = [];
  allMembers: TeamMemberDetails[] = [];
  selection = new SelectionModel<TeamMember>(true, []);
  allRowsSelected: boolean = false;
  displayedColumns: string[] = ['select', 'email', 'onCall'];
  columns: any[] = [];
  dataSource!: MatTableDataSource<TeamMember>;
  isPlatform: boolean = false;
  escalationPolicies!: EscalationPolicy[];
  escalationPoliciesJson?: string;
  exampleEscalationPolicy: string = JSON.stringify([{ "escalateTo": [{ "id": "user@carvana.com" }, { "id": "user2@carvana.com" }], "escalationIntervalInMinutes": 20 }], null, 4);
  processingWorkflows!: ProcessingWorkflow[];
  processingWorkflowJson?: string;
  exampleProcessingWorkflow: string = JSON.stringify([{ "workflowName": "TestWorkflow", "rules": [{ "ruleName": "OnlyProcessWest", "expression": "alert.Data.Essentials.AlertRule.Contains(\"east\")" }, { "ruleName": "NoonDownTime", "expression": "currentTime.LocalDateTime.TimeOfDay.Hours >=12 && currentTime.LocalDateTime.TimeOfDay.Hours < 13" }] }], null, 4);
  testAlertPayload?: string;
  testExpression?: string;
  teamsUrl = '/oncall/teams';
  supportHoursTimeZone!: string;
  supportHoursDays!: string[];
  supportHoursStartTime!: string;
  supportHoursEndTime!: string;
  supportHoursEnabled!: boolean;
  splunkOnCallIntegrated!: boolean;
  splunkOnCallTeamName!: string;
  splunkOnCallRoutingKey!: string;
  timeZones: any[] = [
    {
      name: "(UTC-10:00) Hawaii",
      value: "Pacific/Honolulu"
    },
    {
      name: "(UTC-9:00) Alaska",
      value: "America/Juneau"
    },
    {
      name: "(UTC-08:00) Pacific Time",
      value: "America/Los_Angeles"
    },
    {
      name: "(UTC-07:00) Mountain Time",
      value: "America/Denver"
    },
    {
      name: "(UTC-06:00) Central Time",
      value: "America/Chicago"
    },
    {
      name: "(UTC-05:00) Eastern Time",
      value: "America/Indiana/Indianapolis"
    }
  ];
  private window = window;
  private readonly _destroying$ = new Subject<void>();
  @ViewChild('memberTable') teamTable!: MatTable<any>;

  constructor(
    private readonly userProfileService: UserProfileService,
    private environmentService: EnvironmentService,
    private onCallService: OnCallService,
    private dialog: MatDialog,
    private readonly loadingService: LoadingService,
    @Inject(ActivatedRoute) private readonly route: ActivatedRoute,
    private readonly toast: ToastService
  ) {
    this.environment = this.environmentService.getEnvironment();
    this.pageEnabled = this.environment.featureToggles.onCallEnabled === 'true';
  }

  ngOnInit(): void {
    this.dataSource = new MatTableDataSource<TeamMember>([]);
    this.route.params.subscribe(params => {
      this.teamName = params['id'];
    });
    this.selection.changed.subscribe(() => {
      this.allRowsSelected = this.isAllSelected();
    });
    this.columns = this.getColumns();

    // Get user information from claims
    const claims = this.userProfileService.getActiveClaims();
    if (claims) {
      // Check if user is a platform team member
      this.userProfileService.isPlatformTeamMember().subscribe(isPlatformMember => {
        this.isPlatform = isPlatformMember;
      });
    }

    this.getTeam(this.teamName);
    this.getAllTeamMemberDetails();
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

  getColumns(): any[] {
    let columns =
      [
        {
          columnDef: 'email',
          header: 'Email',
          cell: (member: TeamMember) => `${member.id}`
        },
        {
          columnDef: 'onCall',
          header: 'On Call'
        }
      ]
    return columns;
  }

  handleError(error: HttpErrorResponse, message: string): void {
    this.loadingService.hide('editTeam');
    this.toast.error(`${message}: ${error.status} ${error.statusText}`);
    this.openErrorDialog(`${message}: ${error.status} ${error.statusText}`);
  }

  openErrorDialog(errorMessage: string): void {
    this.dialog.open(ErrorDialogComponent, {
      data: { message: errorMessage }
    });
  }

  openAddTeamMemberDialog(): void {
    this.loadingService.show('editTeam');
    let teamMemberIds = this.team.members.map(member => member.id.toLowerCase());
    let sortedMembers = this.allMembers
      .filter(member => !teamMemberIds.includes(member.email.toLowerCase()))
      .sort((a, b) => a.email.localeCompare(b.email));

    const dialogRef = this.dialog.open(AddTeamMemberDialogComponent, {
      data: { members: sortedMembers }
    });

    dialogRef.afterClosed().subscribe({
      next: (members) => {
        this.updateTeamMembers(members);
      },
      error: () => { },
    });
    this.loadingService.hide('editTeam');
  }

  goToTeamsPage(): void {
    this.window.location.href = this.teamsUrl;
  }

  getAllTeamMemberDetails(): void {
    this.loadingService.show('editTeam');
    this.allMembers = new Array<TeamMemberDetails>();
    this.onCallService.getAllTeamMemberDetails()
      .subscribe({
        next: (members) => {
          this.allMembers = members;
          this.loadingService.hide('editTeam');
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error, 'Error fetching team members');
        },
      });
  }

  getTeam(name: string): void {
    this.loadingService.show('editTeam');
    this.onCallService.getTeam(name)
      .subscribe({
        next: (team) => {
          this.team = team;
          this.escalationPoliciesJson = JSON.stringify(team.escalationPolicies, null, 4);
          this.processingWorkflowJson = this.processWorkflows(team.processingWorkflows || []);
          this.supportHoursDays = team.supportHours?.days ?? [];
          this.supportHoursStartTime = team.supportHours?.startTime ?? "";
          this.supportHoursEndTime = team.supportHours?.endTime ?? "";
          this.supportHoursEnabled = team.supportHours?.enabled ?? false;
          this.supportHoursTimeZone = team.supportHours?.timeZone ?? "";
          this.splunkOnCallIntegrated = team.splunkOnCallIntegrated ?? false;
          this.splunkOnCallTeamName = team.splunkOnCallTeamName ?? "";
          this.splunkOnCallRoutingKey = team.splunkOnCallRoutingKey ?? "";
          this.dataSource.data = this.team.members;
          this.selection.clear();
          this.loadingService.hide('editTeam');
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error, 'Error fetching team');
        },
      });
  }

  updateTeam(updatedTeam: Team): void {
    this.loadingService.show('editTeam');
    updatedTeam.splunkOnCallIntegrated = this.splunkOnCallIntegrated;
    updatedTeam.splunkOnCallTeamName = this.splunkOnCallTeamName;
    updatedTeam.splunkOnCallRoutingKey = this.splunkOnCallRoutingKey;

    this.onCallService.createOrUpdateTeam(updatedTeam)
      .subscribe({
        next: () => {
          this.getTeam(this.teamName);
          this.toast.success('Team updated successfully');
          this.loadingService.hide('editTeam');
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error, 'Error updating team');
        }
      });
  }

  processWorkflows(processingWorkflows: ProcessingWorkflow[]): string {
    const processedWorkflows: ProcessingWorkflow[] = processingWorkflows.map((workflow: ProcessingWorkflow) => {
      return {
        workflowName: workflow.workflowName,
        rules: workflow.rules.map((rule) => ({
          ruleName: rule.ruleName,
          expression: rule.expression
        })),
      };
    });

    return JSON.stringify(processedWorkflows, null, 4);
  }

  testProcessingWorkflow(): void {
    try {
    let formattedExpression = this.formatExpression(this.testExpression!);
    let workflow: any = {
      alert: JSON.parse(this.testAlertPayload!),
      expression: formattedExpression
    }

    this.onCallService.testWorkflow(workflow)
      .subscribe({
        next: (response) => {
          let message = response.body[0].isSuccess
            ? 'The expression evaluated to true and would prevent the alert from being processed'
            : 'The expression evaluated to false and would allow the alert to be processed';
          this.toast.info(message);
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error, error.error.exceptionMessage);
        }
      });
    } catch (error) {
      this.toast.error(`Error testing expression: ${error}`);
    }
  }

  formatExpression(expression: string): string {
    const formattedExpression = expression.trim().replace(/\\/g, '');
    return formattedExpression;
  }

  removeMembers(context: any, selectedMembers: TeamMember[]): void {
    context.loading = true;
    let updatedTeam: Team = context.getMembersToRemove(selectedMembers);
    context.updateTeam(updatedTeam);
    context.selection.clear();
  }

  getMembersToRemove(teamMembers: TeamMember[]): Team {
    let onCallTeamMemberIsSelected = teamMembers.find(
      (selected: TeamMember) => selected.id === this.team.onCallTeamMember.id
    );

    let updatedOnCallTeamMember = onCallTeamMemberIsSelected
      ? { ...this.team.onCallTeamMember, id: '' }
      : this.team.onCallTeamMember;

    let updatedMembers = this.team.members.filter(
      (member: TeamMember) => !teamMembers.some((selected) => selected.id === member.id)
    );

    let updatedTeam: Team = { ...this.team, members: updatedMembers, onCallTeamMember: updatedOnCallTeamMember };
    return updatedTeam;
  }

  updateOnCallTeamMember(teamMember: TeamMember): void {
    let updatedTeam: Team = { ...this.team, onCallTeamMember: teamMember };
    this.updateTeam(updatedTeam);
  }

  updateTeamMembers(teamMembers: TeamMemberDetails[]): void {
    // Handle case when teamMembers is null, undefined or not an array
    if (!teamMembers || !Array.isArray(teamMembers)) {
      return; // Nothing to update if there are no members to add
    }

    let updatedMembers = [...this.team.members, ...teamMembers.map(member => ({
      id: member.email
    }))];

    let updatedTeam: Team = { ...this.team, members: updatedMembers };
    this.updateTeam(updatedTeam);
  }

  isOnCall(member: TeamMember): boolean {
    return this.team.onCallTeamMember.id === member.id;
  }

  updateEscalationPolicies(): void {
    this.loadingService.show('editTeam');
    try {
      this.escalationPolicies = JSON.parse(this.escalationPoliciesJson ?? '[]');
      let invalidEscalateToMembers = this.findInvalidEscalateToMembers();

      if (invalidEscalateToMembers.length > 0) {
        this.handleInvalidEscalateToMembers(invalidEscalateToMembers);
        this.loadingService.hide('editTeam');
        return;
      }
    } catch (error) {
      this.openErrorDialog(`Error: ${error}`);
      this.loadingService.hide('editTeam');
      return;
    }

    let updatedTeam: Team = { ...this.team, escalationPolicies: this.escalationPolicies };
    this.updateTeam(updatedTeam);
    this.loadingService.hide('editTeam');
  }

  updateProcessingWorkflows(): void {
    this.loadingService.show('editTeam');
    try {
      this.processingWorkflows = JSON.parse(this.processingWorkflowJson ?? '[]');
    } catch (error) {
      this.openErrorDialog(`Error: ${error}`);
      this.loadingService.hide('editTeam');
      return;
    }

    let updatedTeam: Team = { ...this.team, processingWorkflows: this.processingWorkflows };
    this.updateTeam(updatedTeam);
    this.loadingService.hide('editTeam');
  }

  findInvalidEscalateToMembers(): TeamMember[] {
    return this.escalationPolicies
      .flatMap(policy => policy.escalateTo)
      .filter(member => !this.team.members.some(teamMember => teamMember.id.toLowerCase() === member.id.toLowerCase()));
  }

  handleInvalidEscalateToMembers(invalidMembers: TeamMember[]): void {
    const invalidMemberIds = invalidMembers.map(member => member.id).join(', ');
    const errorMessage = `These users are not a member of ${this.team.name}: ${invalidMemberIds}`;
    this.openErrorDialog(errorMessage);
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected == numRows;
  }

  isUpdateOnCallButtonEnabled(): boolean {
    let onlyOneMemberSelected = this.selection.selected.length === 1;
    let selectedMember = this.selection.selected[0]?.id.toLocaleLowerCase();
    let currentOnCallMember = this.team.onCallTeamMember.id.toLocaleLowerCase();
    return onlyOneMemberSelected && selectedMember !== currentOnCallMember;
  }

  isRemoveMembersButtonEnabled(): boolean {
    let atLeastOneMemberSelected = this.selection.selected.length > 0;
    let currentOnCallMember = this.team.onCallTeamMember.id.toLocaleLowerCase();
    let selectedMembers = this.selection.selected.map(member => member.id.toLocaleLowerCase());
    let onCallMemberNotSelected = selectedMembers.filter(member => member === currentOnCallMember).length === 0;
    return atLeastOneMemberSelected && onCallMemberNotSelected;
  }

  selectAllRows() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.dataSource.data.forEach(row => this.selection.select(row));
  }

  confirmAction(
    message: string,
    confirmActionMethod: (context: any, selectedMembers: TeamMember[]) => void,
    selectedMembers: TeamMember[]
  ): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: { confirmationText: message },
    });

    dialogRef.afterClosed().subscribe({
      next: (confirmed) => {
        if (confirmed) {
          confirmActionMethod(this, selectedMembers);
        }
      },
      error: () => { },
    });
  }

  updateSupportHours(): void {
    this.loadingService.show('editTeam');

    let supportHours: SupportHours = {
      days: this.supportHoursDays,
      startTime: this.supportHoursStartTime,
      endTime: this.supportHoursEndTime,
      timeZone: this.supportHoursTimeZone,
      enabled: this.supportHoursEnabled
    };

    let updatedTeam: Team = { ...this.team, supportHours: supportHours };
    this.updateTeam(updatedTeam);
    this.loadingService.hide('editTeam');
  }

  isSupportHoursFormValid(): boolean {
    return (this.supportHoursDays.length > 0 &&
    this.supportHoursStartTime.length > 0 &&
    this.supportHoursEndTime.length > 0 &&
    this.supportHoursTimeZone.length > 0) ||
    (this.team.supportHours?.enabled === true && this.supportHoursEnabled === false);
  }

  showSplunkOnCallControls(): boolean {
    return this.splunkOnCallIntegrated === true || this.team.splunkOnCallIntegrated === true;
  }

  isSaveTeamButtonEnabled(): boolean {
    return (this.splunkOnCallTeamName.length > 0 && this.splunkOnCallRoutingKey.length > 0) ||
    (this.splunkOnCallIntegrated === false && this.team.splunkOnCallIntegrated === true)
  }
}
